import { useEffect, useState, useMemo } from 'react';
import { Button, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../Redux/Store';
import { useModalStatus } from '../../commonHooks/useModalStatus';
import { useFeedlotId } from '../../commonHooks/useFeedlotId';
import { useDebouncedEffect } from '../useDebounceEffect';
import {
    deleteRider,
    fetchRiderDetails,
    updateRiderDetail,
} from '../../Redux/Actions/riderActions';
import { setRiderFilter } from '../../Redux/Reducer/riderSlice';
import { RIDER_INITIAL_STATE } from '../../Redux/Constants/riderInitialState';
import RiderModal from './RiderModal';
import SettingsItemDetail from './FMSettings/SettingsItemDetail';
import { DeleteItemModal } from './FMSettings/DeleteItemModal';
import Constants from '../../utils/Constants';
import StorageService from '../../Services/storage/storage.service';
import { riderConstants } from '../../utils/constants/RiderConstants';
import { FmSettingsConstant } from '../../utils/constants/FmSettingsConstants';
import { IFilter, IRiderDetail } from '../../utils/Interface/RiderInterface';

const RiderDetails = () => {
    const { filter, loader } = useAppSelector(state => state.rider);
    const storage = useMemo(() => new StorageService(), []);
    const feedlotId = useFeedlotId();
    const dispatch = useAppDispatch();
    const {
        isOpen: isRiderModalOpen,
        open: openRiderModal,
        close: closeRiderModal,
    } = useModalStatus();
    const {
        isOpen: isDeleteModalOpen,
        open: openDeleteModal,
        close: closeDeleteModal,
    } = useModalStatus();
    const [searchQuery, setSearchQuery] = useState<string | undefined>(
        undefined,
    );
    const [riderDetails, setRiderDetails] = useState<IRiderDetail[]>([]);
    const [selectedRider, setSelectedRider] = useState<IRiderDetail>(
        RIDER_INITIAL_STATE.createRiderInitial,
    );

    const fetchRiderData = (riderFilter: IFilter = filter): void => {
        if (feedlotId) {
            dispatch(
                fetchRiderDetails(feedlotId, setRiderDetails, riderFilter),
            );
        }
    };

    const handleEditRiderDetail = (rider: IRiderDetail): void => {
        setSelectedRider(rider);
        openRiderModal();
    };

    const setRiderFilterInStorage = (filterValues: IFilter) => {
        storage.setValue(
            riderConstants.riderFilter,
            filterValues,
            Constants.Storage.LOCAL,
        );
    };

    const handleFilter = (filterName: string, filterValue: string) => {
        const updatedFilter = { ...filter, [filterName]: filterValue };
        fetchRiderData(updatedFilter);
        dispatch(setRiderFilter(updatedFilter));
        setRiderFilterInStorage(updatedFilter);
    };

    const closeModalWithChanges = (isEdited: boolean): void => {
        if (isEdited) {
            fetchRiderData(filter);
        }
        closeRiderModal();
    };

    const resetFilter = (): void => {
        const updatedFilter = RIDER_INITIAL_STATE.filter;
        fetchRiderData(updatedFilter);
        setSearchQuery('');
        dispatch(setRiderFilter(updatedFilter));
        setRiderFilterInStorage(updatedFilter);
    };

    const handleCreateRider = (): void => {
        if (feedlotId) {
            setSelectedRider({
                ...RIDER_INITIAL_STATE.createRiderInitial,
                feedLotId: feedlotId,
            });
        }
        openRiderModal();
    };

    const handleDeleteModalHandler = (rider: IRiderDetail) => {
        setSelectedRider(rider);
        openDeleteModal();
    };

    const handleDeleteRider = (shouldTakeAction: boolean) => {
        if (feedlotId && shouldTakeAction) {
            if (!selectedRider.isAssociated || selectedRider.active) {
                const isActiveAndAssociated =
                    selectedRider.isAssociated && selectedRider.active;
                const action = isActiveAndAssociated
                    ? updateRiderDetail
                    : deleteRider;
                const updatedRiderDetail = isActiveAndAssociated
                    ? { ...selectedRider, active: false }
                    : selectedRider;
                dispatch(
                    action(feedlotId, updatedRiderDetail, { fetchRiderData }),
                );
            }
        }
        closeDeleteModal();
    };

    useDebouncedEffect(
        () => {
            if (searchQuery !== undefined) {
                handleFilter('name', searchQuery);
            }
        },
        [searchQuery],
        1000,
    );

    useEffect(() => {
        if (feedlotId) {
            const riderFilter = storage.getValue(riderConstants.riderFilter);
            if (Object.keys(riderFilter).length > 0) {
                dispatch(setRiderFilter(riderFilter));
            }
            if (!riderFilter.name) {
                if (riderFilter.show) {
                    fetchRiderData(riderFilter);
                } else {
                    dispatch(
                        fetchRiderDetails(feedlotId, setRiderDetails, filter),
                    );
                }
            } else {
                setSearchQuery(riderFilter?.name || filter.name);
            }
        }
    }, [feedlotId, dispatch, storage]);

    return (
        <div className="pens-container">
            <h5 className="my-3">{riderConstants.riderDetails.heading}</h5>
            <div className="d-flex align-items-end">
                <Col xs={3} className="d-flex flex-column">
                    <label>{riderConstants.riderDetails.rider}</label>
                    <input
                        className="text-secondary my-2"
                        type="text"
                        placeholder={riderConstants.riderDetails.placeholder}
                        maxLength={riderConstants.riderDetails.maxLength}
                        value={searchQuery ? searchQuery : ''}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </Col>
                <Col xs={3}>
                    <Button
                        className="secondary my-2 mx-4"
                        onClick={resetFilter}
                    >
                        {riderConstants.clearFilter}
                    </Button>
                </Col>
                <Col xs={6} className="d-flex justify-content-end">
                    <Button className="my-2" onClick={handleCreateRider}>
                        {riderConstants.createNewRiders.heading}
                    </Button>
                </Col>
            </div>
            <div
                className="o-switch btn-group show-toggle my-2"
                data-toggle="buttons"
                role="group"
            >
                {FmSettingsConstant.filterOptions.map(option => (
                    <label
                        key={option.value}
                        className={`btn btn-secondary py-1 ${
                            filter.show === option.value
                                ? FmSettingsConstant.active.value
                                : ''
                        }`}
                    >
                        <input
                            type="radio"
                            onChange={() =>
                                handleFilter(option.name, option.value)
                            }
                            checked={filter.show === option.value}
                        />
                        {option.label}
                    </label>
                ))}
            </div>
            <SettingsItemDetail<IRiderDetail>
                columnDetail={riderConstants.riderColumnDetail}
                itemDetail={riderDetails}
                handleEditDetail={handleEditRiderDetail}
                handleDeleteModalHandler={handleDeleteModalHandler}
                loader={loader}
            />
            {isRiderModalOpen && (
                <RiderModal
                    showModal={isRiderModalOpen}
                    closeModalWithChanges={closeModalWithChanges}
                    editRiderDetails={selectedRider}
                    showActiveField={true}
                />
            )}
            {isDeleteModalOpen && (
                <DeleteItemModal
                    item={selectedRider}
                    handleDeleteItem={handleDeleteRider}
                    showModal={isDeleteModalOpen}
                    itemName={riderConstants.riderDetails.name}
                ></DeleteItemModal>
            )}
        </div>
    );
};

export default RiderDetails;
